import { memo } from "react";
import { Box, Card, Grid, Typography } from "@mui/material";
import {
  BlockOutlined,
  CheckCircleOutline,
  ErrorOutline,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import BulkRunTable from "./BulkRunTable";

function SummaryCard({ icon, count, label, caption, color }) {
  return (
    <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
      <Box display="flex" alignItems="center" gap={1}>
        {icon}
        <Typography variant="h4" sx={{ color }}>
          {count}
        </Typography>
      </Box>
      <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
        {label}
      </Typography>
      {!!caption && (
        <Typography variant="caption" color="text.lighter">
          {caption}
        </Typography>
      )}
    </Card>
  );
}

/** Step 4 — what happened, bucketed, with the leftovers made obvious. */
function BulkResultsStep({
  rows,
  counts,
  assignedCost,
  companyName,
}) {
  const { t } = useTranslation();

  const remaining =
    counts.unsupported +
    counts.failed +
    counts.stopped +
    counts.waiting +
    counts.eligible;

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <SummaryCard
            icon={<CheckCircleOutline sx={{ color: "success.main" }} />}
            count={counts.assigned}
            color="success.main"
            label={t("bulkAssign.summaryAssigned")}
            caption={
              <>
                {assignedCost > 0 &&
                  t("bulkAssign.estimatedCost", {
                    amount: assignedCost.toFixed(2),
                  })}
                {counts.alreadyAssigned > 0 && (
                  <>
                    {assignedCost > 0 && <br />}
                    {t("bulkAssign.alreadyAssignedCaption", {
                      total: counts.alreadyAssigned,
                    })}
                  </>
                )}
              </>
            }
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard
            icon={<ErrorOutline sx={{ color: "error.main" }} />}
            count={counts.failed}
            color="error.main"
            label={t("bulkAssign.summaryFailed")}
            caption={t("bulkAssign.failedCaption")}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard
            icon={<BlockOutlined sx={{ color: "warning.main" }} />}
            count={counts.unsupported}
            color="warning.main"
            label={t("bulkAssign.summaryUnsupported", {
              company: companyName,
            })}
            caption={t("bulkAssign.unsupportedCaption")}
          />
        </Grid>
      </Grid>

      {remaining === 0 && (
        <Typography variant="subtitle2" color="success.main" sx={{ mb: 1 }}>
          {t("bulkAssign.allAssigned", { company: companyName })}
        </Typography>
      )}

      <BulkRunTable
        rows={rows}
        counts={counts}
        companyName={companyName}
        height={340}
      />
    </Box>
  );
}

export default memo(BulkResultsStep);
